import { useState, useEffect, useCallback, useRef } from "react";
import { LayoutGrid, List, Search, Plus, X, Filter } from "lucide-react";
import ExportButton from "../components/MyTask/ExportButton";

import { PopupCtx }      from "../components/MyTask/PopupCtx";
import { TABS }          from "../components/MyTask/data";
import { fetchListing }  from "../utils/api";
import axiosInstance, { getUserRole }     from "../API/interceptor";

import WorkspaceDropdown from "../components/MyTask/WorkspaceDropdown";
import TeamDropdown      from "../components/MyTask/TeamDropdown";
import CommentPanel      from "../components/MyTask/CommentPanel";
import TaskDetailPanel   from "../components/MyTask/TaskDetailPanel";
import CreateTaskDrawer  from "../components/MyTask/CreateTaskDrawer";
import FilterPanel       from "../components/MyTask/FilterPanel";
import WorkTracker       from "../components/MyTask/WorkTracker";
import ListView          from "../components/MyTask/ListView";
import CardView          from "../components/MyTask/CardView";
import DraftView         from "../components/MyTask/DraftView";
import StandingTask      from "./MyTask/StandingTask";
import KanbanBoard       from "../components/MyTask/KanbanBoard";

const DEFAULT_PAGE_SIZE = 10;

/* ══ STATIC PILL CONFIG ══ */
const STAT_PILLS_CFG = [
  { label: "Open",           apiKey: "open",             from: "#7C3AED" },
  { label: "In Progress",    apiKey: "in_progress",      from: "#0D9488" },
  { label: "On Hold",        apiKey: "on_hold",          from: "#DB2777" },
  { label: "Complete",       apiKey: "completed",        from: "#059669" },
  { label: "Approval Await", apiKey: "approval_pending", from: "#D97706" },
  { label: "Closed",         apiKey: "closed",           from: "#64748B" },
];

// pill apiKey → Flask status string
const PILL_TO_STATUS = {
  open:             "open",
  in_progress:      "in progress",
  on_hold:          "on hold",
  completed:        "completed",
  approval_pending: "approval pending",
  closed:           "closed",
};

/* ══ MAIN APP ══ */
export default function MyTaskPage() {
  const [activeTab,    setActiveTab]    = useState("My Task");
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [drawerDraft,  setDrawerDraft]  = useState(null);
  const [filterOpen,     setFilterOpen]     = useState(false);
  const [filterClearKey, setFilterClearKey] = useState(0);
  const [commentTask,  setCommentTask]  = useState(null);
  const [detailTask,   setDetailTask]   = useState(null);
  const [popup,        setPopup]        = useState(null);

  // ── Workspace — numeric code strings ["221304", "1593"] ───
  const [workspace,     setWorkspace]     = useState([]);
  const [workspaceOpts, setWorkspaceOpts] = useState([]);  // API se aayenge

  // ── Team — task_type value string ─────────────────────────
  const [team,           setTeam]           = useState("");   // "" = All Team
  const [roleLoaded,     setRoleLoaded]     = useState(false);

  const [taskSearch,   setTaskSearch]   = useState("");
  const [view,         setView]         = useState(() => window.innerWidth < 768 ? "grid" : "list");
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkConfirm,  setBulkConfirm]  = useState(false);
  const [bulkLoading,  setBulkLoading]  = useState(false);
  // Bumped after a bulk action succeeds to tell ListView to clear its own
  // internal selectedIds — clearing bulkSelected here only resets this
  // page's mirror copy, not ListView's real selection state.
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [extraFilters, setExtraFilters] = useState({});

  // ── Tasks API state ───────────────────────────────────────
  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // ── Server-side sort ──────────────────────────────────────
  const [sortConfig, setSortConfig] = useState({ col: 10, dir: "desc" });
  const sortRef = useRef({ col: 10, dir: "desc" });
  useEffect(() => { sortRef.current = sortConfig; }, [sortConfig]);

  // ── Stats state ───────────────────────────────────────────
  const [stats,         setStats]         = useState({});
  const [statsLoading,  setStatsLoading]  = useState(false);
  const [activeStatKey, setActiveStatKey] = useState(null);
  const [wtRefreshKey,  setWtRefreshKey]  = useState(0);

  // ── Search debounce ───────────────────────────────────────
  const searchDebounceRef = useRef(null);
  const searchMountedRef  = useRef(false);

  useEffect(() => {
    axiosInstance.get("/task_assignment/get_product_codes")
      .then(res => {
        const arr = Array.isArray(res.data?.product_codes) ? res.data.product_codes
                  : Array.isArray(res.data) ? res.data : [];
        const opts = arr.map(item => {
          if (typeof item === "object") {
            const key = Object.keys(item)[0];
            return { value: String(key), label: item[key] };
          }
          return null;
        }).filter(Boolean);
        setWorkspaceOpts(opts);
      })
      .catch(() => {});
  }, []);


  // ── Fetch user role — product owner / admin ──────────────
  const subordinateIdsRef = useRef([]);
  const roleLoadedRef     = useRef(false);
  const [currentUserId,             setCurrentUserId]             = useState(null);
  const [productOwnerWorkspaceCodes, setProductOwnerWorkspaceCodes] = useState([]);

  useEffect(() => {
    getUserRole()
      .then(res => {
        if (res.data?.success) {
          subordinateIdsRef.current = res.data.subordinate_ids || [];
          setCurrentUserId(res.data.user_id ?? null);
        }
      })
      .catch(() => {})
      .finally(() => {
        roleLoadedRef.current = true;
        setRoleLoaded(true);
      });
    axiosInstance.get("/task_assignment/get_product_owner_workspaces")
      .then(res => {
        const list = Array.isArray(res.data?.workspaces) ? res.data.workspaces : [];
        setProductOwnerWorkspaceCodes(list.map(String));
      })
      .catch(() => {});
  }, []);

  // ── Stats payload — status exclude ───────────────────────
  const teamRef         = useRef(team);
  const workspaceRef    = useRef(workspace);
  const extraFiltersRef = useRef(extraFilters);
  const taskSearchRef   = useRef(taskSearch);
  useEffect(() => { teamRef.current         = team;         }, [team]);
  useEffect(() => { workspaceRef.current    = workspace;    }, [workspace]);
  useEffect(() => { extraFiltersRef.current = extraFilters; }, [extraFilters]);
  useEffect(() => { taskSearchRef.current   = taskSearch;   }, [taskSearch]);

  const buildStatsPayload = useCallback(() => ({
    product_code:          workspaceRef.current,
    status:                [],
    priority:              extraFiltersRef.current.priority              || [],
    assigned_to:           extraFiltersRef.current.assigned_to           || [],
    created_by:            extraFiltersRef.current.created_by            || [],
    informer:              extraFiltersRef.current.informer              || [],
    observer:              extraFiltersRef.current.observer              || [],
    functional_categories: extraFiltersRef.current.functional_categories || [],
    task_type:             teamRef.current,
    created_at:            extraFiltersRef.current.created_at            || "",
    task_deadline:         extraFiltersRef.current.task_deadline         || "",
    start_date:            extraFiltersRef.current.start_date            || "",
    subordinate_ids:       subordinateIdsRef.current,
  }), []);

  // ── Tasks payload — status include ───────────────────────
  const buildTasksPayload = useCallback(() => ({
    product_code:          workspaceRef.current,
    status:                extraFiltersRef.current.status                || [],
    priority:              extraFiltersRef.current.priority              || [],
    assigned_to:           extraFiltersRef.current.assigned_to           || [],
    created_by:            extraFiltersRef.current.created_by            || [],
    informer:              extraFiltersRef.current.informer              || [],
    observer:              extraFiltersRef.current.observer              || [],
    functional_categories: extraFiltersRef.current.functional_categories || [],
    task_type:             teamRef.current,
    created_at:            extraFiltersRef.current.created_at            || "",
    task_deadline:         extraFiltersRef.current.task_deadline         || "",
    start_date:            extraFiltersRef.current.start_date            || "",
    subordinate_ids:       subordinateIdsRef.current,
    order_column:          sortRef.current.col,
    order_dir:             sortRef.current.dir,
  }), []);

  // ── Tasks fetch ───────────────────────────────────────────
  const loadTasks = useCallback(async (pageNum = 1, ps = pageSize) => {
    setLoading(true);
    try {
      const result = await fetchListing({
        draw:         pageNum,
        start:        (pageNum - 1) * ps,
        length:       ps,
        search_value: taskSearchRef.current,
        ...buildTasksPayload(),
      });
      setTasks(result.data || []);
      setTotalRecords(result.recordsFiltered || result.recordsTotal || 0);
      setPage(pageNum);
    } catch (err) {
      console.error("Tasks fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [pageSize, buildTasksPayload]);

  // ── Stats fetch ───────────────────────────────────────────
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axiosInstance.post("/task_assignment/get_task_statistics", {
        search: taskSearch,
        ...buildStatsPayload(),
      });
      if (res.data?.success) setStats(res.data.statistics || {});
    } catch (err) {
      console.error("Stats fetch error:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [taskSearch, buildStatsPayload]);

  // ── Auto reload — workspace / team / extraFilters change pe ──
  useEffect(() => {
    if (!roleLoaded) return;
    if (activeTab === "My Task") {
      loadTasks(1, pageSize);
      loadStats();
    }
  }, [activeTab, workspace, team, extraFilters, roleLoaded]); // eslint-disable-line

  // ── Server-side sort handler ──────────────────────────────
  const handleServerSort = useCallback((colIndex) => {
    const prev    = sortRef.current;
    const newDir  = prev.col === colIndex && prev.dir === "asc" ? "desc" : "asc";
    const next    = { col: colIndex, dir: newDir };
    sortRef.current = next;
    setSortConfig(next);
    loadTasks(1, pageSize);
  }, [loadTasks, pageSize]);

  // ── Search — debounce 400ms ───────────────────────────────
  useEffect(() => {
    if (!searchMountedRef.current) { searchMountedRef.current = true; return; }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (activeTab === "My Task" && roleLoadedRef.current) {
        loadTasks(1, pageSize);
        loadStats();
      }
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [taskSearch]); // eslint-disable-line

  // ── Pill click ────────────────────────────────────────────
  const handlePillClick = (apiKey) => {
    const isAlreadyActive = activeStatKey === apiKey;
    const nextKey = isAlreadyActive ? null : apiKey;
    setActiveStatKey(nextKey);
    setExtraFilters(prev => ({
      ...prev,
      status: nextKey ? [PILL_TO_STATUS[nextKey]] : [],
    }));
  };

  // ── Clear all filters ─────────────────────────────────────
  const clearAllFilters = () => {
    setExtraFilters({});
    setActiveStatKey(null);
    setWorkspace([]);
    setTeam("");
    setTaskSearch("");
    setFilterClearKey(k => k + 1);
  };

  // ── Active filters bar build ──────────────────────────────
  const handleBulkComplete = async () => {
    if (bulkSelected.size === 0) return;
    setBulkLoading(true);
    try {
      await axiosInstance.post("/task_assignment/bulk_complete", { task_ids: Array.from(bulkSelected) });
      setBulkSelected(new Set());
      setSelectionResetKey(k => k + 1);
      setBulkConfirm(false);
      loadTasks(1, pageSize);
    } catch(e) { console.error("Bulk complete failed", e); }
    finally { setBulkLoading(false); }
  };

  const buildActiveFilters = () => {
    const list = [];

    if (activeStatKey) {
      const pill = STAT_PILLS_CFG.find(p => p.apiKey === activeStatKey);
      if (pill) list.push({
        key: "status_pill",
        label: `Status: ${pill.label}`,
        onRemove: () => { setActiveStatKey(null); setExtraFilters(prev => ({ ...prev, status: [] })); }
      });
    } else if (extraFilters.status?.length) {
      list.push({
        key: "status",
        label: `Status: ${extraFilters.status.join(", ")}`,
        onRemove: () => setExtraFilters(prev => ({ ...prev, status: [] }))
      });
    }

    if (extraFilters.priority?.length) list.push({
      key: "priority",
      label: `Priority: ${extraFilters.priority.join(", ")}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, priority: [] }))
    });
    if (extraFilters.assigned_to?.length) list.push({
      key: "assigned_to",
      label: `Responsible: ${(extraFilters.assigned_to_names || []).join(", ") || `${extraFilters.assigned_to.length} selected`}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, assigned_to: [], assigned_to_names: [] }))
    });
    if (extraFilters.created_by?.length) list.push({
      key: "created_by",
      label: `Accountable: ${(extraFilters.created_by_names || []).join(", ") || `${extraFilters.created_by.length} selected`}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, created_by: [], created_by_names: [] }))
    });
    if (extraFilters.observer?.length) list.push({
      key: "observer",
      label: `Consulted: ${(extraFilters.observer_names || []).join(", ") || `${extraFilters.observer.length} selected`}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, observer: [], observer_names: [] }))
    });
    if (extraFilters.informer?.length) list.push({
      key: "informer",
      label: `Informed: ${(extraFilters.informer_names || []).join(", ") || `${extraFilters.informer.length} selected`}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, informer: [], informer_names: [] }))
    });
    if (extraFilters.functional_categories?.length) list.push({
      key: "func_cat",
      label: `Category: ${extraFilters.functional_categories.join(", ")}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, functional_categories: [] }))
    });
    if (extraFilters.created_at) list.push({
      key: "created_at",
      label: `Created: ${extraFilters.created_at}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, created_at: "" }))
    });
    if (extraFilters.task_deadline) list.push({
      key: "deadline",
      label: `Deadline: ${extraFilters.task_deadline}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, task_deadline: "" }))
    });
    if (extraFilters.start_date) list.push({
      key: "start_date",
      label: `Start Date: ${extraFilters.start_date}`,
      onRemove: () => setExtraFilters(prev => ({ ...prev, start_date: "" }))
    });
    if (workspace?.length) list.push({
      key: "workspace",
      label: `Workspace: ${workspace.map(v => workspaceOpts.find(o => o.value === v)?.label || v).join(", ")}`,
      onRemove: () => setWorkspace([])
    });
    if (team) list.push({
      key: "team",
      label: `Team: ${team}`,
      onRemove: () => setTeam("")
    });
    if (taskSearch) list.push({
      key: "search",
      label: `Search: "${taskSearch}"`,
      onRemove: () => setTaskSearch("")
    });

    return list;
  };

  const activeFiltersList = buildActiveFilters();

  return (
    <PopupCtx.Provider value={{ setPopup, setCommentTask, setDetailTask }}>
      <div className="h-auto bg-slate-50 font-sans rounded-lg">
        <CreateTaskDrawer
          open={drawerOpen}
          resumeDraftData={drawerDraft}
          onClose={() => { setDrawerOpen(false); setDrawerDraft(null); }}
          onTaskCreated={() => { loadTasks(1, pageSize); loadStats(); setWtRefreshKey(k => k + 1); }}
        />
        <FilterPanel
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onApply={f => { setExtraFilters(f); setFilterOpen(false); setActiveStatKey(null); }}
          clearKey={filterClearKey}
          syncFilters={extraFilters}
        />
        <CommentPanel    task={commentTask} onClose={() => setCommentTask(null)} onCommented={() => { loadTasks(page, pageSize); loadStats(); }} />
        <TaskDetailPanel
          taskId={detailTask?._id || detailTask}
          onClose={() => setDetailTask(null)}
          onAction={() => { loadTasks(page, pageSize); loadStats(); }}
          onTaskUpdate={(update) => setTasks(prev => prev.map(t => String(t._id) === String(update._id) ? { ...t, ...update } : t))}
        />

        {/* ══ WHITE HEADER ══ */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-3 md:px-5">

            {/* Tabs row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 min-w-0 overflow-hidden border-b border-[#f1f5f9]">
              <div className="flex overflow-x-auto min-w-0 flex-shrink scrollbar-hide">
                {TABS.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 md:px-4 py-3.5 text-xs md:text-sm font-semibold whitespace-nowrap border-none bg-transparent cursor-pointer transition-all -mb-px border-b-2 ${
                      activeTab === tab ? "text-violet-600 font-bold border-[#7C3AED]" : "text-slate-500 hover:text-slate-700 border-transparent"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Right side — pills or create button */}
              {["Kanban", "Standing Task", "Draft", "Work Tracker"].includes(activeTab) ? (
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="flex items-center gap-1.5 text-white border-none rounded-xl px-3 md:px-4 py-2 text-xs md:text-sm font-semibold cursor-pointer flex-shrink-0 whitespace-nowrap hover:opacity-90 active:scale-95 transition-all self-start sm:self-auto mb-2 sm:mb-0 bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]"
                >
                  <Plus size={14} /> <span>Create New Task</span>
                </button>
              ) : (
                <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto flex-shrink-0 py-2 pr-1 scrollbar-hide">
                  {STAT_PILLS_CFG.map(({ label, apiKey, from }) => {
                    const count    = stats[apiKey] ?? 0;
                    const isActive = activeStatKey === apiKey;

                    if (statsLoading) {
                      return (
                        <div
                          key={apiKey}
                          className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-lg flex-shrink-0 animate-pulse min-w-[88px] border"
                          style={{ background: `${from}0f`, borderColor: `${from}28` }}
                        >
                          <div className="h-2.5 rounded w-11" style={{ background: `${from}25` }} />
                          <div className="h-4 rounded-md w-5" style={{ background: `${from}25` }} />
                        </div>
                      );
                    }

                    return (
                      <div
                        key={apiKey}
                        onClick={() => handlePillClick(apiKey)}
                        className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 md:py-1.5 rounded-lg cursor-pointer transition-all hover:opacity-80 flex-shrink-0 border ${
                          isActive ? "-translate-y-px" : ""
                        }`}
                        style={{
                          background: isActive ? `${from}22` : `${from}0f`,
                          borderColor: isActive ? from : `${from}28`,
                          boxShadow:  isActive ? `0 0 0 2px ${from}35` : "none",
                        }}
                      >
                        <span className="text-[10px] md:text-xs font-medium whitespace-nowrap" style={{ color: from }}>
                          {label}
                        </span>
                        <span
                          className="text-[10px] md:text-xs font-black px-1 md:px-1.5 py-0.5 rounded-md"
                          style={{ background: `${from}20`, color: from }}
                        >
                          {String(count).padStart(2, "0")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Filter controls bar — only show on My Task tab */}
            {!["Kanban", "Standing Task", "Draft", "Work Tracker"].includes(activeTab) && (
              <div className="flex flex-wrap items-center gap-2 py-2.5">

                {/* 1. Workspace dropdown */}
                <div className="w-full sm:w-44 md:w-56 flex-shrink-0">
                  <WorkspaceDropdown
                    value={workspace}
                    onChange={setWorkspace}
                    options={workspaceOpts}
                  />
                </div>

                {/* 2. Team dropdown */}
                <div className="w-full sm:w-32 md:w-44 flex-shrink-0">
                  <TeamDropdown
                    value={team}
                    onChange={setTeam}
                  />
                </div>

                {/* 3. Search input — flex-1 so it fills remaining space */}
                <div className="w-full sm:w-72 md:w-96 flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-50 transition-all">
                    <Search size={14} className="text-slate-400 flex-shrink-0" />
                    <input
                      value={taskSearch}
                      onChange={e => setTaskSearch(e.target.value)}
                      placeholder="Search by Task ID / Name..."
                      className="border-none outline-none text-sm text-gray-700 flex-1 min-w-0 bg-transparent"
                    />
                    {taskSearch && (
                      <button
                        onClick={() => setTaskSearch("")}
                        className="bg-transparent border-none cursor-pointer text-slate-400 flex items-center hover:text-slate-600 transition-colors"
                      >
                        <X size={13} />
                      </button>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-nowrap flex-shrink-0 ml-auto">
                  {activeTab === "My Task" && (
                    <>
                      {/* View toggle — Grid / List */}
                      <div className="flex bg-slate-100 rounded-xl p-0.5 gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => setView("grid")}
                          className={`w-8 h-8 rounded-lg border-none cursor-pointer flex items-center justify-center transition-all ${
                            view === "grid" ? "bg-white text-violet-600 shadow-sm" : "bg-transparent text-slate-400 hover:text-slate-600"
                          }`}
                        >
                          <LayoutGrid size={14} />
                        </button>
                        <button
                          onClick={() => setView("list")}
                          className={`w-8 h-8 rounded-lg border-none cursor-pointer flex items-center justify-center transition-all ${
                            view === "list" ? "bg-white text-violet-600 shadow-sm" : "bg-transparent text-slate-400 hover:text-slate-600"
                          }`}
                        >
                          <List size={14} />
                        </button>
                      </div>

                      {/* Bulk complete button — shows when tasks selected */}
                      {bulkSelected.size > 0 && (
                        <div className="flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap">
                          <span className="text-[11px] font-semibold text-[#4338CA] bg-[#EEF2FF] border border-[#C7D2FE] px-2.5 py-[3px] rounded-[20px] whitespace-nowrap">
                            {bulkSelected.size} selected
                          </span>
                          {!bulkConfirm ? (
                            <button
                              onClick={() => setBulkConfirm(true)}
                              className="flex items-center gap-[5px] text-xs font-semibold px-3 py-[5px] rounded-lg border-none bg-[#16A34A] text-white cursor-pointer"
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                              Mark Complete
                            </button>
                          ) : (
                            <>
                              <span className="text-[11px] text-[#374151]">Confirm?</span>
                              <button
                                onClick={handleBulkComplete}
                                disabled={bulkLoading}
                                className={`text-xs font-semibold px-2.5 py-[5px] rounded-lg border-none text-white ${
                                  bulkLoading ? "bg-[#9CA3AF] cursor-wait" : "bg-[#16A34A] cursor-pointer"
                                }`}
                              >
                                {bulkLoading ? "..." : "Yes"}
                              </button>
                              <button
                                onClick={() => setBulkConfirm(false)}
                                className="text-xs px-2.5 py-[5px] rounded-lg border border-[#E2E8F0] bg-white text-[#64748B] cursor-pointer"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => { setBulkSelected(new Set()); setBulkConfirm(false); }}
                            className="text-[11px] px-2 py-1 rounded-[7px] border border-[#E2E8F0] bg-white text-[#94A3B8] cursor-pointer"
                          >
                            ✕
                          </button>
                        </div>
                      )}

                      {/* Export All */}
                      <ExportButton
                        filters={{
                          product_code:          workspace,
                          task_type:             team,
                          search_value:          taskSearch,
                          status:                extraFilters.status                || [],
                          priority:              extraFilters.priority              || [],
                          assigned_to:           extraFilters.assigned_to           || [],
                          created_by:            extraFilters.created_by            || [],
                          informer:              extraFilters.informer              || [],
                          observer:              extraFilters.observer              || [],
                          functional_categories: extraFilters.functional_categories || [],
                          created_at:            extraFilters.created_at            || "",
                          task_deadline:         extraFilters.task_deadline         || "",
                          start_date:            extraFilters.start_date            || "",
                        }}
                        totalRecords={totalRecords}
                        hasFilters={
                          workspace.length > 0      ||
                          team !== ""               ||
                          taskSearch !== ""         ||
                          Object.values(extraFilters).some(v =>
                            Array.isArray(v) ? v.length > 0 : v !== "" && v != null
                          )
                        }
                      />
                    </>
                  )}

                  {/* Filter button */}
                  <button
                    onClick={() => setFilterOpen(true)}
                    className="flex items-center gap-1.5 bg-violet-50 border border-violet-100 text-violet-600 rounded-lg px-2.5 md:px-3.5 py-2 text-xs md:text-sm font-semibold cursor-pointer hover:bg-violet-100 transition-colors flex-shrink-0 whitespace-nowrap"
                  >
                    <Filter size={13} /> <span className="hidden sm:inline">Filter</span>
                  </button>

                  {/* Create New Task */}
                  <button
                    onClick={() => setDrawerOpen(true)}
                    className="flex items-center gap-1.5 text-white border-none rounded-xl px-3 md:px-4 py-2 text-xs md:text-sm font-semibold cursor-pointer flex-shrink-0 whitespace-nowrap hover:opacity-90 active:scale-95 transition-all bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]"
                  >
                    <Plus size={14} /> <span>Create New Task</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ══ CONTENT ══ */}
        <div>
          {activeTab === "My Task" && (
            <>
              {/* Active Filters Bar */}
              {activeFiltersList.length > 0 && (
                <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 border-b border-gray-100 flex-wrap">
                  <span className="text-xs font-semibold text-slate-500 flex-shrink-0">Active Filters:</span>
                  {activeFiltersList.map(f => (
                    <span
                      key={f.key}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-slate-200 text-slate-600 shadow-sm"
                    >
                      {f.label}
                      <button
                        onClick={f.onRemove}
                        className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 hover:bg-red-100 hover:text-red-500 text-slate-400 border-none cursor-pointer transition-colors text-[10px] font-bold leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={clearAllFilters}
                    className="px-2.5 py-1 rounded-full text-xs font-semibold border border-red-200 text-red-500 bg-white hover:bg-red-50 cursor-pointer transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              )}

              {/* ListView / CardView */}
              {view === "list"
                ? <ListView
                    tasks={tasks}
                    loading={loading}
                    onCommentClick={task => setCommentTask({ _id: task._id, name: task.task_type })}
                    onBulkReady={ids => setBulkSelected(new Set(ids))}
                    selectionResetKey={selectionResetKey}
                    onRefresh={() => { loadTasks(page, pageSize); loadStats(); }}
                    serverSortConfig={sortConfig}
                    onServerSort={handleServerSort}
                    currentUserId={currentUserId}
                    productOwnerWorkspaceCodes={productOwnerWorkspaceCodes}
                  />
                : loading
                  ? (
                    <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
                      <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Loading tasks…
                    </div>
                  )
                  : <CardView
                      tasks={tasks}
                      loading={loading}
                      onTaskClick={task => setDetailTask(task)}
                      onCommentClick={task => setCommentTask({ _id: task._id, name: task.task_type })}
                    />
              }

              {/* Pagination */}
              {!loading && tasks.length > 0 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-wrap gap-3 bg-white">

                  {/* Left — Show entries */}
                  <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span>Show</span>
                    <select
                      value={pageSize}
                      onChange={e => {
                        const newSize = Number(e.target.value);
                        setPageSize(newSize);
                        loadTasks(1, newSize);
                      }}
                      className="border border-gray-200 rounded px-1.5 py-1 text-xs text-slate-600 bg-white cursor-pointer focus:outline-none focus:border-violet-400"
                    >
                      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span>entries</span>
                    <span className="ml-2 text-slate-400">
                      Showing <b className="text-gray-700">{(page - 1) * pageSize + 1}</b> to{" "}
                      <b className="text-gray-700">{Math.min(page * pageSize, totalRecords)}</b> of{" "}
                      <b className="text-gray-700">{totalRecords}</b> entries
                    </span>
                  </div>

                  {/* Right — Pagination buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      disabled={page === 1}
                      onClick={() => loadTasks(page - 1, pageSize)}
                      className="px-3 py-1 rounded border text-xs cursor-pointer font-medium transition-colors bg-white border-gray-200 text-slate-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {(() => {
                      const pages = [];
                      const delta = 2;
                      const left  = Math.max(2, page - delta);
                      const right = Math.min(totalPages - 1, page + delta);

                      pages.push(1);
                      if (left > 2) pages.push("...");
                      for (let i = left; i <= right; i++) pages.push(i);
                      if (right < totalPages - 1) pages.push("...");
                      if (totalPages > 1) pages.push(totalPages);

                      return pages.map((p, idx) =>
                        p === "..." ? (
                          <span key={`dots-${idx}`} className="px-2 py-1 text-xs text-slate-400">...</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => loadTasks(p, pageSize)}
                            className={`px-2.5 py-1 rounded border text-xs min-w-[32px] cursor-pointer font-medium transition-colors ${
                              p === page
                                ? "bg-[linear-gradient(135deg,#7C3AED,#6D28D9)] text-white border-transparent"
                                : "bg-white border-[#E5E7EB] text-[#6B7280]"
                            }`}
                          >
                            {p}
                          </button>
                        )
                      );
                    })()}

                    <button
                      disabled={page === totalPages}
                      onClick={() => loadTasks(page + 1, pageSize)}
                      className="px-3 py-1 rounded border text-xs cursor-pointer font-medium transition-colors bg-white border-gray-200 text-slate-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "Work Tracker"  && <WorkTracker refreshKey={wtRefreshKey} />}
          {activeTab === "Kanban"        && <KanbanBoard />}
          {activeTab === "Standing Task" && <StandingTask />}
          {activeTab === "Draft"         && <DraftView onResume={d => { setDrawerDraft(d); setDrawerOpen(true); }} />}

          {!["My Task", "Work Tracker", "Standing Task", "Draft", "Kanban"].includes(activeTab) && (
            <div className="bg-white rounded-2xl py-20 text-center text-slate-400 border border-gray-100 shadow-sm">
              <div className="text-5xl mb-3">🚧</div>
              <p className="text-sm font-semibold m-0">{activeTab} — Coming Soon</p>
            </div>
          )}
        </div>

        {/* Popup portal */}
        {popup && (
          <div className="fixed inset-0 z-[2147483647] pointer-events-none">
            <div className="pointer-events-auto">{popup}</div>
          </div>
        )}
      </div>
    </PopupCtx.Provider>
  );
}